import {
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  SafetyOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Card, Image, Input, message, Modal, Spin, Upload } from "antd";
import React, { useEffect, useRef, useState } from "react";
import useAxios from "../../../hooks/useAxios";
import CropImage from "../components/CropImage";
import StatusIndicator, {
  VerifiedIndicator,
} from "./components/StatusIndicator";
import StatusMessage from "./components/StatusMessage";
import ValidationPopup from "../components/ValidationPopup";
import { capitalizeSentence } from "../utils/commonFunctions";

const MB = 3;

const PanCard = ({
  application,
  getAadharAndPanDetails,
  loadApplication,
  uploadedFile,
  panDetails,
  url,
  disabledDueToAadhar,
  aiSettings,
}) => {
  const ID = application?._id;
  const [load, setLoad] = useState(false);
  const api = useAxios();
  const [file, setFile] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  const [crop, setCrop] = useState();
  const [imgSrc, setImgSrc] = useState("");
  const [errorPan, setErrorPan] = useState("");
  const [error, setError] = useState("");
  const [errorVerification, setErrorVerification] = useState({});
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [pan, setPan] = useState({
    pan_no: "",
    acknowledgement_no: "",
  });
  const [panType, setPanType] = useState("pan_number");
  const [uploadModal, setUploadModal] = useState(false);
  const panRef = useRef("");

  const toggle = () => setModal(!modal);
  const previewToggle = () => setPreviewModal(!previewModal);

  useEffect(() => {
    if (!application) return;
    setPan({
      ...pan,
      pan_no: application?.panCardNo || "",
      acknowledgement_no: application?.acknowledgementNo || "",
    });
    setPanType(application?.panNoType || "pan_number");
    panRef.current = application?.panNoType || "pan_number";
  }, [
    application?.panCardNo,
    application?.acknowledgementNo,
    application?.panNoType,
  ]);

  const resetCrop = () => {
    setImgSrc("");
    setCrop(undefined);
    setAspectRatio(16 / 9);
    setFile(null);
  };

  function onSelectFile(fileData) {
    const { file, fileList } = fileData;
    if (file && fileList.length > 0) {
      if (file.size > MB * 1024 * 1024) {
        message.warning(`Image size must be less than ${MB}MB`, 2);
        return;
      }
      setCrop(undefined);
      const aspect = 130 / 140;
      setAspectRatio(aspect);
      setFile(file);
      const reader = new FileReader();
      reader.addEventListener(
        "load",
        () => (setImgSrc(reader.result?.toString() || ""), toggle())
      );
      reader.readAsDataURL(file);
    }
  }

  const onClickImgHandler = async (file) => {
    setUploadModal(false);
    setError("");
    setErrorPan("");
    const form = new FormData();
    form.append("panCardImage", file);
    form.append("panType", panRef.current);
    try {
      setLoad(true);
      const result = await api.put(`/candidate/panCardImage/${ID}`, form, {
        tokenType: "candidate",
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const { response } = result?.data?.OCRDetails;

      if ("error" in response) {
        setError(response?.error);
      }

      const promise = [loadApplication(false, false, "pan")];
      promise.push(getAadharAndPanDetails());
      setPanType(panRef.current);
      await Promise.all(promise);
      resetCrop();
      if (panRef.current === "pan_number") {
        if (aiSettings?.isActive) {
          if ("error" in response) {
            setError(response?.error);
          } else {
            message.success(`PAN Card uploaded successfully`, 2);
          }
        } else {
          if ("error" in response) {
            setError(response?.error);
          }
          message.success(`PAN Card uploaded successfully`, 2);
        }
      } else {
        if (aiSettings?.isActive) {
          if ("error" in response) {
            setError(response?.error);
          } else {
            message.success(`Acknowledgement Number uploaded successfully`, 2);
          }
        } else {
          if ("error" in response) {
            setError(response?.error);
          }
          message.success(`Acknowledgement Number uploaded successfully`, 2);
        }
      }
    } catch (err) {
      console.log(err?.response?.data?.error?.OCRDetails?.response?.error);
      setError(err?.response?.data?.error?.OCRDetails?.response?.error);
      setLoad(false);
    } finally {
      setLoad(false);
    }
  };

  const reUploadHandlerPan = async () => {
    try {
      setLoad(true);
      await api.put(
        `/candidate/resetPanDetails/${application?._id}`,
        {},
        {
          tokenType: "candidate",
        }
      );
      await Promise.all([
        getAadharAndPanDetails(),
        loadApplication(false, false, ""),
      ]);
      setOpenWarningModal(false);
      setErrorVerification({});
      message.success("PAN reset Successfully", 2);
    } catch (err) {
      console.error(err);
    } finally {
      setLoad(false);
    }
  };

  const panVerify = async (e) => {
    e.preventDefault();
    if (panRef.current === "pan_number") {
      if (pan?.pan_no?.trim() === "" || pan?.pan_no?.trim().length !== 10) {
        setErrorPan("PAN Number must be 10 digits");
        return;
      }
    } else {
      if (
        pan?.acknowledgement_no?.trim() === "" ||
        pan.acknowledgement_no.trim().length < 10 ||
        pan.acknowledgement_no.trim().length > 20
      ) {
        setErrorPan("Acknowledgement Number must be between 10 to 20 digits.");
        return;
      }
    }
    setErrorPan("");

    try {
      setLoad(true);
      const response = await api.post(
        `/candidateDocuments/VerifyPanNo`,
        {
          panNoType: panRef.current,
          id_number:
            panRef.current === "pan_number" ? pan?.pan_no?.toUpperCase() : "",
          acknowledgementNo:
            panRef.current !== "pan_number" ? pan?.acknowledgement_no : "",
          candidateId: ID,
        },
        { tokenType: "candidate" }
      );

      let msg = "";
      if (panRef.current === "pan_number") {
        const { isVerified, verificationDetails, panResponse } = response.data;
        if (!isVerified) {
          setErrorVerification(verificationDetails);
          setOpenWarningModal(true);
        }
        msg = "Pan Card verify Successfully";
      } else {
        msg = "Acknowledgement Number save Successfully";
      }
      message.success(msg, 2);
      setPan({
        pan: "",
        acknowledgement_no: "",
      });
    } catch (err) {
      let error = "";
      console.error(err);
      if (typeof err.response.data.error === "object") {
        if (err.response.data.error.status_code == 401) {
          error = "For now service unavailable.";
        } else {
          error = err.response.data.error.message;
        }
      } else {
        error = err.response.data.error || err.response.data.message;
      }
      setErrorPan(error);
    } finally {
      await Promise.all([
        loadApplication(false, false, ""),
        getAadharAndPanDetails(),
      ]);
      setLoad(false);
    }
  };

  const uploaded = uploadedFile && uploadedFile?.length !== 0;

  return (
    <Spin
      style={{ backgroundColor: "rgba(0, 0, 0, 0.47)" }}
      spinning={load}
      tip={
        <div>
          <p className="text-white font-weight-normal">
            Uploading another file won't affect this one.
          </p>
        </div>
      }
      size="large"
    >
      <Card
        className={`mb-4 bg-white`}
        style={{ border: "1px solid grey" }}
        title={null}
      >
        <div className="d-flex flex-column px-4" style={{ gap: 10 }}>
          <div className="d-flex justify-content-center">
            <StatusIndicator
              filledStatus={application?.panCardImageFilledStatus}
              status={application?.panStatus}
            />
          </div>
          <div>
            <h5 className="text-dark text-center font-weight-bold mb-1">
              {panType === "pan_number" ? "PAN Card" : "Acknowledgement Number"}
            </h5>
            <StatusMessage
              filledStatus={application?.panCardImageFilledStatus}
              status={application?.panStatus}
              reason={application?.panRejection}
              numberReason={
                panDetails?.rejectionReason
                  ? `${
                      panType === "pan_number" ? "PAN" : "Acknowledgement"
                    } Number verification failed. ${
                      panDetails?.rejectionReason
                    }`
                  : undefined
              }
              type={
                panType === "pan_number" ? "PAN Card" : "Acknowledgement Number"
              }
            />
          </div>
          <div>
            {uploaded && (
              <hr
                style={{ borderTop: "1px solid #D5D5D5" }}
                className="mb-3"
              ></hr>
            )}
            <div className="d-flex align-items-center flex-column gap-1">
              {uploaded && (
                <Image
                  style={{ objectFit: "contain" }}
                  src={url}
                  width={200}
                  height={100}
                />
              )}
              {!application?.panStatus && (
                <div className="d-flex align-items-center justify-content-center w-100">
                  <Button
                    data-testid = "upload-pan-ack-trigger"
                    onClick={() => setUploadModal(true)}
                    style={{ border: "1px dashed #6c757d" }}
                    icon={<UploadOutlined />}
                    disabled={disabledDueToAadhar}
                  >
                    {uploaded
                      ? `Re Upload ${
                          panType === "pan_number" ? "PAN" : "Acknowledgement"
                        }`
                      : "Upload PAN Card / Acknowledgement"}
                  </Button>
                </div>
              )}
              {error && <p className="text-danger">{capitalizeSentence(error)}</p>}
            </div>
            {uploaded && (
              <>
                <hr className="my-3" style={{ borderTop: "1px solid #D5D5D5" }}></hr>
                {panType === "pan_number" && (
                  <div className="d-flex align-items-center gap-1">
                    <label
                      className="font-weight-normal mb-0"
                      style={{ fontSize: "1rem" }}
                    >
                      PAN&nbsp;Number:&nbsp;
                    </label>
                    <div className="w-100">
                      <Input
                        status={errorPan ? "error" : null}
                        value={
                          panDetails?.documentStatus === true ||
                          (panDetails?.isFilledStatus === true &&
                            panDetails?.aadharVerificationDetails
                              ?.status_code === 200)
                            ? "XXX XXX " +
                              application?.panCardNo?.slice(-4)?.toUpperCase()
                            : pan?.pan_no?.toUpperCase()
                        }
                        disabled={
                          panDetails?.documentStatus === true ||
                          (panDetails?.isFilledStatus === true &&
                            panDetails?.aadharVerificationDetails
                              ?.status_code === 200) ||
                          disabledDueToAadhar
                        }
                        onChange={(e) => {
                          let input = e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, "");
                          if (input.length > 10) {
                            input = input.substr(0, 10);
                          }
                          setPan({
                            ...pan,
                            pan_no: input,
                          });
                        }}
                        type="text"
                        placeholder="Enter PAN Number"
                      />
                      {errorPan !== "" && (
                        <div className="row input-group">
                          <div className="col-12 input-group">
                            <b className="text-danger">{errorPan}</b>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {panType === "pan_acknowledgement" && (
                  <div className="d-flex align-items-center gap-1">
                    <label
                      className="font-weight-normal mb-0"
                      style={{ fontSize: "1rem" }}
                    >
                      Ack&nbsp;Number:&nbsp;
                    </label>
                    <div className="w-100">
                      <Input
                        status={errorPan ? "error" : null}
                        value={
                          application?.acknowledgementStatus === true ||
                          application?.acknowledgementFilledStatus === true
                            ? "XXX XXX " +
                              application?.acknowledgementNo
                                ?.slice(-4)
                                ?.toUpperCase()
                            : pan?.acknowledgement_no?.toUpperCase()
                        }
                        disabled={
                          application?.acknowledgementStatus === true ||
                          application?.acknowledgementFilledStatus === true ||
                          disabledDueToAadhar
                        }
                        onChange={(e) => {
                          setPan({
                            ...pan,
                            acknowledgement_no: e.target.value,
                          });
                        }}
                        type="text"
                        placeholder="Enter Acknowledgement Number"
                      />
                      {errorPan !== "" && (
                        <div className="row input-group">
                          <div className="col-12 input-group">
                            <b className="text-danger">{errorPan}</b>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {panType === "pan_number" && (
                  <>
                    {panDetails?.documentStatus === true ||
                    (panDetails?.isFilledStatus === true &&
                      panDetails?.aadharVerificationDetails?.status_code ===
                        200) ? (
                      <>
                        <p
                          className="text-center mb-1"
                          style={{
                            fontSize: "16px",
                            color: "#00AC1A",
                            marginTop: 10,
                            display: "flex",
                            gap: 10,
                            justifyContent: "center",
                          }}
                        >
                          <CheckCircleFilled
                            style={{ fontSize: "1.2rem", color: "#00AC1A" }}
                          />
                          Verified Successfully.
                        </p>
                      </>
                    ) : (
                      <>
                        <hr className="my-3" style={{ borderTop: "1px solid #D5D5D5" }}></hr>
                        <div className="d-flex justify-content-center">
                          <button
                            className={`btn ${
                              disabledDueToAadhar
                                ? "btn-secondary"
                                : "btn-primary"
                            }`}
                            disabled={disabledDueToAadhar}
                            onClick={panVerify}
                            style={{
                              // border: "1px solid #777777",
                              // background: "#fff",
                              // outline: "none",
                              // borderRadius: "4px",
                              // padding: "7px 20px",
                              // color: "#777",
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              fontSize: "15px",
                            }}
                          >
                            <SafetyOutlined style={{ fontSize: "18px" }} />
                            {panDetails?.aadharVerificationDetails?.message
                              ? "Re "
                              : ""}
                            Verify PAN Number
                          </button>
                        </div>
                        {panDetails?.aadharVerificationDetails?.message &&
                          panDetails?.documentStatus !== false && (
                            <p
                              className="text-center mb-1"
                              style={{
                                fontSize: "16px",
                                color: "red",
                                marginTop: 10,
                                display: "flex",
                                gap: 10,
                                justifyContent: "center",
                              }}
                            >
                              {panDetails?.aadharVerificationDetails?.message}
                            </p>
                          )}
                      </>
                    )}
                  </>
                )}
                {panType === "pan_acknowledgement" && (
                  <>
                    {application?.acknowledgementStatus === true ||
                    application?.acknowledgementFilledStatus === true ? (
                      <>
                        <p
                          className="text-center mb-1"
                          style={{
                            fontSize: "16px",
                            color: "#00AC1A",
                            marginTop: 10,
                            display: "flex",
                            gap: 10,
                            justifyContent: "center",
                          }}
                        >
                          <CheckCircleFilled
                            style={{ fontSize: "1.2rem", color: "#00AC1A" }}
                          />
                          Verified Successfully.
                        </p>
                      </>
                    ) : (
                      <>
                        <hr className="my-3" style={{ borderTop: "1px solid #D5D5D5" }}></hr>
                        <div className="d-flex justify-content-center">
                          <button
                            disabled={disabledDueToAadhar}
                            onClick={panVerify}
                            style={{
                              border: "1px solid #777777",
                              background: "#fff",
                              outline: "none",
                              borderRadius: "4px",
                              padding: "7px 20px",
                              color: "#777",
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              fontSize: "15px",
                            }}
                          >
                            <SafetyOutlined style={{ fontSize: "18px" }} />
                            Save Acknowledgement Number
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
                {application?.panStatus &&
                  (application?.acknowledgementStatus ||
                    panDetails?.documentStatus) && (
                    <div className="d-flex justify-content-center mt-3">
                      <VerifiedIndicator />
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      </Card>
      <Modal
        title={`Crop Aadhar Card`}
        centered
        open={modal}
        zIndex={999999}
        onOk={() => toggle()}
        closable={false}
        width={700}
        footer={null}
        className="crop-ant-modal-body"
      >
        <CropImage
          key={file}
          setCrop={setCrop}
          crop={crop}
          imgSrc={imgSrc}
          file={file}
          toggle={toggle}
          aspectRatio={aspectRatio}
          setAspectRatio={setAspectRatio}
          onClickImgHandler={onClickImgHandler}
        />
      </Modal>
      <Modal
        title=""
        className="p-0 custom_modal__"
        centered
        open={uploadModal}
        width={600}
        closeIcon={
          <CloseOutlined style={{ color: "white", fontSize: "16px" }} />
        }
        footer={null}
        onCancel={() => {
          setUploadModal(false);
          setPanType(application?.panNoType);
        }}
      >
        <div>
          <div className="modal-header d-flex justify-content-between align-items-center active-color-modal text-white w-100">
            <h5
              className="modal-title text-white text-capitalize font-weight-bold"
              style={{ fontSize: "18px", fontWeight: 400 }}
            >
              Upload PAN Card / Acknowledgement
            </h5>
          </div>
          <div className="row py-3 px-4 ">
            <div className="col-12 p-3 d-flex flex-column gap-1">
              <div
                className="d-flex flex-column align-items-center"
                style={{ gap: 10 }}
              >
                <p
                  className="text-dark text-center mb-1"
                  style={{ fontSize: "18px" }}
                >
                  Upload PAN Card
                </p>
                <Upload
                  listType="picture"
                  maxCount={1}
                  fileList={null}
                  onChange={(value) => onSelectFile(value)}
                  onPreview={(file) => {
                    previewToggle();
                  }}
                  showUploadList={{
                    showRemoveIcon: false,
                  }}
                  beforeUpload={(file) => {
                    const isValid =
                      file.type === "image/png" ||
                      file.type === "image/jpeg" ||
                      file.type === "image/jpg";

                    if (!isValid) {
                      message.error(
                        "Only PNG, JPG, JPEG and JFIF files are allowed."
                      );
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  accept="image/png, image/jpg, image/jpeg"
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <Button
                      data-testid = "upload-pan-card"
                      style={{ border: "1px dashed #6c757d" }}
                      icon={<UploadOutlined />}
                      onClick={() => {
                        panRef.current = "pan_number";
                        setErrorPan("");
                      }}
                      disabled={disabledDueToAadhar}
                    >
                      Browse
                    </Button>
                  </div>
                </Upload>
              </div>
              <div class="d-flex align-items-center my-1">
                <div class="flex-grow-1 border-top"></div>
                <span class="px-2 rounded-pill border">Or</span>
                <div class="flex-grow-1 border-top"></div>
              </div>
              <div
                className="d-flex flex-column align-items-center"
                style={{ gap: 10 }}
              >
                <p
                  className="text-dark text-center mb-1"
                  style={{ fontSize: "18px" }}
                >
                  Upload PAN Acknowledgement Document
                </p>
                <Upload
                  listType="picture"
                  maxCount={1}
                  fileList={null}
                  onChange={(value) => onSelectFile(value)}
                  onPreview={(file) => {
                    previewToggle();
                  }}
                  showUploadList={{
                    showRemoveIcon: false,
                  }}
                  beforeUpload={(file) => {
                    const isValid =
                      file.type === "image/png" ||
                      file.type === "image/jpeg" ||
                      file.type === "image/jpg";

                    if (!isValid) {
                      message.error(
                        "Only PNG, JPG, JPEG and JFIF files are allowed."
                      );
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  accept="image/png, image/jpg, image/jpeg"
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <Button
                      data-testid = "upload-ack"
                      style={{ border: "1px dashed #6c757d" }}
                      icon={<UploadOutlined />}
                      onClick={() => {
                        panRef.current = "pan_acknowledgement";
                        setErrorPan("");
                      }}
                      disabled={disabledDueToAadhar}
                    >
                      Browse
                    </Button>
                  </div>
                </Upload>
              </div>
              <hr className="mb-0"></hr>
              <div>
                <b>Disclaimer:</b> The name on your Aadhaar card must match the
                name on your PAN Card/Acknowledgement for successful
                verification.
              </div>
            </div>
          </div>
        </div>
      </Modal>
      {openWarningModal && (
        <ValidationPopup
          application={application}
          details={panDetails}
          errorVerification={errorVerification}
          openWarningModal={openWarningModal}
          updateDetailsHandler={undefined}
          reUploadHandler={undefined}
          reUploadHandlerPan={reUploadHandlerPan}
          setOpenWarningModal={setOpenWarningModal}
          fileType={"pan"}
        />
      )}
    </Spin>
  );
};

export default PanCard;
